<template lang="pug">
section
    .loader(
        v-if="loading"
        v-loading="true"
    )
    el-alert(
        v-else-if="currentMenuItem.id === '__orphans'"
        type="warning"
        :title="$t('Something went wrong! Here are some menu items that could not be positioned inside the tree.')"
        :closable="false"
        effect="dark"
    )
    .menu-item-form(v-else-if="currentMenuItem")
        .i18n-select-container(v-if="i18n && allowedToTranlsate && languageOptions.length > 1 && defaultLanguage")
            el-form-item.flex-row(:label="$t('Language')")
                el-select(
                    :placeholder="$t('Default Language ({code})', { code: defaultLanguage.code.toUpperCase() })"
                    v-model="language"
                    value-key="code"
                )
                    template(v-slot:prefix)
                        span.i18n-flag.circle.fi.fis(
                            v-if="activeTranslation"
                            :class="`fi-${activeTranslation.language.flag || getLanguageFlag(activeTranslation.language.code)}`"
                        )
                        span.i18n-flag.circle.fi.fis(
                            v-else
                            :class="`fi-${defaultLanguage.flag || getLanguageFlag(defaultLanguage.code)}`"
                        )
                    el-option(
                        v-for="item in languageOptions"
                        :key="item.code"
                        :label="getLanguageName(item.code)"
                        :value="item"
                        :disabled="!activeTranslation && item.code === defaultLanguage.code"
                    )
                        .flex-row
                            .col.grow
                                span.i18n-flag.circle.fi.fis(:class="`fi-${item.flag || getLanguageFlag(item.code)}`")
                                span {{ getLanguageName(item.code) }}
                            .col.gray-text
                                small.uppercase(v-if="item.code === defaultLanguage.code") {{ $t('Default') }}
                                small.uppercase(v-else-if="!findTranslation(item.code)") {{ $t('New') }}
                el-button.delete-button(
                    v-if="activeTranslation"
                    circle
                    size="mini"
                    type="danger"
                    icon="el-icon-fa-trash-alt far"
                    :loading="saving === 'delete-translation'"
                    @click="deleteTranslation(activeTranslation)"
                )
        el-collapse(v-model="collapseItem")
            el-collapse-item.settings(:title="$t('Menu Item Settings')" name="settings")
                el-form(
                    v-if="pageForm.id"
                    label-position="top"
                    :model="menuItemForm"
                    :rules="settingRules"
                    ref="settingsForm"
                )
                    el-row.no-margin(:gutter="40")
                        el-col(:lg="14" :md="12")
                            el-form-item(:label="$t('Name')" prop="title")
                                el-input(
                                    v-model="title"
                                    maxlength="255"
                                    :show-word-limit="true"
                                    :placeholder="activeTranslation ? menuItemForm.title : ''"
                                )
                        el-col(:lg="10" :md="12")
                            el-form-item(:label="$t('URL Key')" prop="path")
                                .url-key-input
                                    el-input(
                                        v-model="path"
                                        maxlength="255"
                                        :show-word-limit="true"
                                        :placeholder="activeTranslation ? menuItemForm.path : ''"
                                    )
                                    el-tooltip(effect="dark" :content="$t('Copy URL')" placement="top")
                                        el-button.url-copy-button(
                                            icon="el-icon-fa-copy far"
                                            :disabled="!publicUrl"
                                            @click.prevent="copyPublicUrl"
                                        )
                    .flex-row
                        el-form-item(:label="$t('Page Type')")
                            el-select.is-full-width(
                                placeholder=""
                                v-model="view"
                                :disabled="!!activeTranslation"
                            )
                                // TODO
                                // check for conditions
                                el-option(
                                    v-for="item in menuOptions.itemType.items"
                                    :key="item.view"
                                    :label="item.name"
                                    :value="item.view"
                                )
                        el-form-item(label=" ")
                            el-switch(
                                :active-text="$t('Homepage')"
                                :disabled="!!activeTranslation"
                                v-model="homepage"
                            )
                        el-form-item(label=" ")
                            el-switch(
                                :active-text="$t('for a single domain only')"
                                :value="homepage && homepageRestriction === 'domain'"
                                :disabled="!homepage || !!activeTranslation"
                                @change="toggleHomepageRestriction"
                            )
                        transition(name="slide-decent-left")
                            el-form-item.grow(
                                v-if="homepage && homepageRestriction"
                                :label="$t('Homepage Domain')"
                                :disabled="!!activeTranslation"
                                prop="homepageDomain"
                            )
                                el-select.is-full-width(
                                    placeholder=""
                                    v-model="homepageDomain"
                                    value-key="id"
                                )
                                    el-option(
                                        v-for="domain in activeCms.domains"
                                        :key="domain.id"
                                        :label="domain.base_url"
                                        :value="domain"
                                    )
                    template(v-if="effectiveExtensions.includes('restricted-area')")
                        //- hr.wave

                        //- .restriction-row
                        //-     el-form-item(:label="$t('Accessible by')" :prop="`menuItems.${currentMenuItemIndex}.restriction`")
                        //-         el-select.is-full-width(placeholder="" v-model="restriction")
                        //-             el-option(
                        //-                 v-for="restriction in restrictions"
                        //-                 :key="restriction.key"
                        //-                 :label="restriction.name"
                        //-                 :value="restriction.value"
                        //-             )

                        transition(name="slide-decent-vertical")
                            .flex-row(v-if="!homepage")
                                el-form-item(:label="$t('Accessible by')" prop="restriction")
                                    el-select.is-full-width(
                                        placeholder=""
                                        v-model="restriction"
                                        :disabled="!!activeTranslation"
                                    )
                                        el-option(
                                            v-for="restriction in restrictions"
                                            :key="restriction.key"
                                            :label="restriction.name"
                                            :value="restriction.value"
                                        )
                                transition(name="slide-decent-left")
                                    el-form-item(v-if="['members', 'member-tags'].includes(restriction)" label=" ")
                                        el-switch(
                                            :active-text="$t('Any')"
                                            :value="restriction === 'members'"
                                            :disabled="!!activeTranslation"
                                            @change="toggleMemberRestriction"
                                        )
                        transition(name="slide-decent-vertical")
                            el-form-item(
                                v-if="restriction === 'member-tags'"
                                :label="$t('Only members with the following tags can access this menu item')"
                                prop="tags"
                            )
                                el-select.is-full-width(
                                    ref="memberTagSelect"
                                    multiple
                                    clearable
                                    filterable
                                    allow-create
                                    default-first-option
                                    value-key="id"
                                    :placeholder="$t('Select tags')"
                                    :disabled="!!activeTranslation"
                                    v-model="memberTags"
                                    @change="$refs.memberTagSelect.blur()"
                                )
                                    el-option(
                                        v-for="tag in pageOptions.extensions.restrictedArea.memberTag.items"
                                        :key="tag.id"
                                        :label="tag.name"
                                        :value="tag"
                                    ) {{ tag.name }}

                    .content-status-flex-row
                        .content-status-flex-col.datepickers
                            el-row(:gutter="20")
                                el-col(:span="12")
                                    el-form-item.no-margin.is-full-width(
                                        :label="$t('Publish start')"
                                    )
                                        el-date-picker.is-full-width(
                                            v-model="publishStart"
                                            type="date"
                                            :format="$t('yyyy-MM-dd')"
                                            prefix-icon="far el-icon-fa-calendar-alt"
                                            :picker-options="{firstDayOfWeek:1, disabledDate: disabledPublishStartDate}"
                                            :disabled="!!activeTranslation"
                                        )
                                el-col(:span="12")
                                    el-form-item.no-margin.is-full-width(
                                        :label="$t('Publish end')"
                                    )
                                        el-date-picker.is-full-width(
                                            v-model="publishEnd"
                                            type="date"
                                            :format="$t('yyyy-MM-dd')"
                                            prefix-icon="far el-icon-fa-calendar-alt"
                                            :picker-options="{firstDayOfWeek:1, disabledDate: disabledPublishEndDate}"
                                            :disabled="!!activeTranslation"
                                        )
                        .content-status-flex-col.status-switch
                            el-form-item.no-margin
                                el-switch(
                                    :active-text="$t('Active')"
                                    inactive-color="#ff4949"
                                    :disabled="!!activeTranslation"
                                    v-model="status"
                                )
                        .content-status-flex-col.visibility-switch
                            el-form-item.no-margin
                                el-switch(
                                    :inactive-text="$t('Hidden')"
                                    :active-text="$t('Visible')"
                                    inactive-color="#ff4949"
                                    :disabled="!!activeTranslation"
                                    v-model="visible"
                                )


                    .custom-settings(v-if="!activeTranslation && settings.length && settings.filter(setting => checkConditions(setting)).length")
                        //- hr(v-if="settings.length && settings.filter(setting => checkConditions(setting)).length" key="hr")
                        transition-group(name="slide-decent-vertical" mode="in-out")
                            .setting(
                                v-for="setting in settings" :key="setting.key"
                                v-if="checkConditions(setting)"
                                :class="setting.key"
                            )
                                input-field(
                                    :config="setting"
                                    :content="settingValues"
                                    :options="{ ...pageOptions, ...menuOptions }"
                                    :edit="true"
                                    @change="updateSetting"
                                )
                    .actions.flex-row.mt-8
                        //- .grow
                        .buttons
                            el-button(
                                round
                                type="success"
                                icon="el-icon-fa-save fas"
                                :loading="saving === 'settings'"
                                :disabled="saving !== false && saving !== 'settings'"
                                @click="saveSettings"
                            ) {{ $t('Save Settings') }}

            el-collapse-item.marketing(:title="marketingTitle" name="marketing")
                PageViewsChart.widget(
                    v-if="pageForm.id"
                    :widget="pageviewStattisticsWidget"
                    :page="pageForm"
                    @update-widget-value="setPageviewStatisticWidgetValue"
                )
                template(v-if="enabledExtensions.includes('marketing') && checkRestrictions('ex:marketing')")
                    el-form-item.no-margin
                        el-switch(
                            :active-text="$t('Target')"
                            inactive-color="#ff4949"
                            v-model="marketingTarget"
                        )
                    el-form-item.no-margin
                        el-switch(
                            :active-text="$t('Marketing Campaign Landingpage')"
                            inactive-color="#ff4949"
                            v-model="marketingCampaignLandingPage"
                        )
                    transition(name="slide-decent-vertical")
                        el-form-item.marketing-campaign-select(v-if="marketingCampaignLandingPage" :label="$t('Marketing Campaign')")
                            el-select.is-full-width(
                                :class="{ 'loading': pageOptions.extensions.marketing.campaign.loading }"
                                v-model="marketingCampaign"
                                filterable
                                clearable
                                placeholder=""
                                value-key="id"
                            )
                                el-option(
                                    v-for="campaign in pageOptions.extensions.marketing.campaign.items"
                                    :key="campaign.id"
                                    :label="campaign.name"
                                    :value="campaign"
                                )
                    .actions.flex-row.mt-8
                        //- .grow
                        .buttons
                            el-button(
                                round
                                type="success"
                                icon="el-icon-fa-save fas"
                                :loading="saving === 'marketing'"
                                :disabled="saving !== false && saving !== 'marketing'"
                                @click="saveMarketing"
                            ) {{ $t('Save Marketing') }}
            el-collapse-item.redirect(v-if="view === 'Redirect'" :title="$t('Redirect')" name="content")
                Redirect(v-if="pageForm.id")
                .actions.flex-row.mt-8
                    //- .grow
                    .buttons
                        el-button(
                            round
                            type="success"
                            icon="el-icon-fa-save fas"
                            :loading="saving === 'redirect'"
                            :disabled="saving !== false && saving !== 'redirect'"
                            @click="saveRedirect"
                        ) {{ $t('Save Redirect') }}
            el-collapse-item.reference(v-if="view === 'Reference'" :title="$t('Content from')" name="content")
                Reference(v-if="pageForm.id")
                .actions.flex-row.mt-8
                    //- .grow
                    .buttons
                        el-button(
                            round
                            type="success"
                            icon="el-icon-fa-save fas"
                            :loading="saving === 'reference'"
                            :disabled="saving !== false && saving !== 'reference'"
                            @click="saveReference"
                        ) {{ $t('Save Referenced Content') }}
            el-collapse-item.content(v-else-if="! hideMain" :title="$t('Content')" name="content")
                Main(
                    v-if="pageForm.id"
                    :config="config"
                    :section-options="sectionOptions"
                )
            el-collapse-item.meta(v-if="! hideMeta" :title="$t('Meta')" name="meta")
                Meta(v-if="pageForm.id" :title-placeholder="title")
                .actions.flex-row.mt-8
                    //- .grow
                    .buttons
                        el-button(
                            round
                            type="success"
                            icon="el-icon-fa-save fas"
                            :loading="saving === 'meta'"
                            :disabled="saving !== false && saving !== 'meta'"
                            @click="saveMeta"
                        ) {{ $t('Save Meta') }}
    el-alert(v-else type="info" :title="$t('No menu item selected!')" :closable="false")

    el-dialog.i18n-dialog(
        :title="$t('Translate Menu Item')"
        :visible.sync="translateDialog.visibility"
        :append-to-body="true"
        :width="translateDialog.supportedAutoTranslate ? '780px' : '640px'"
        @closed="resetTranslateDialog"
    )
        .body(v-loading="translateDialog.loading")
            p(
                v-if="translateDialog.form.language"
                v-html="$t('How should the menu item be translated into <b>{language}</b>?', { language: getLanguageName(translateDialog.form.language.code) })"
            )
            .alert.warning.flex-row(v-if="translateDialog.supportedAutoTranslate")
                .icon
                    //- i.el-icon-fa-flask-vial.fas
                    //- <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><!--!Font Awesome Pro 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path d="M224 32h16c8.8 0 16-7.2 16-16s-7.2-16-16-16H224 192 64 32 16C7.2 0 0 7.2 0 16s7.2 16 16 16H32V352c0 53 43 96 96 96c11.2 0 22-1.9 32-5.5c0-14.2 3-28.3 8.8-41.2C157.7 410.5 143.5 416 128 416c-35.3 0-64-28.7-64-64V192H192V352c0 4-.4 7.8-1 11.6l33-53.7V32zM64 32H192V160H64V32zM304 0c-8.8 0-16 7.2-16 16s7.2 16 16 16h16V219.5L201.8 411.6c-6.4 10.4-9.8 22.3-9.8 34.5c0 36.4 29.5 65.9 65.9 65.9H574.1c36.4 0 65.9-29.5 65.9-65.9c0-12.2-3.4-24.1-9.8-34.5L512 219.5V32h16c8.8 0 16-7.2 16-16s-7.2-16-16-16H304zM480 32V224c0 3 .8 5.9 2.4 8.4L536.3 320H295.7l53.9-87.6c1.6-2.5 2.4-5.4 2.4-8.4V32H480zM229 428.4L276 352H556l47 76.4c3.3 5.3 5 11.5 5 17.8c0 18.7-15.2 33.9-33.9 33.9H257.9c-18.7 0-33.9-15.2-33.9-33.9c0-6.3 1.7-12.4 5-17.8z"/></svg>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><!--!Font Awesome Pro 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path class="fa-secondary" opacity=".4" d="M175 389.4l49-79.6V64c17.7 0 32-14.3 32-32s-14.3-32-32-32H160 96 32C14.3 0 0 14.3 0 32S14.3 64 32 64V352c0 53 43 96 96 96c11.2 0 22-1.9 32-5.5c0-18.7 5.2-37.1 15-53.1zM96 64h64v96H96V64z"/><path class="fa-primary" d="M480 0H352 320c-17.7 0-32 14.3-32 32s14.3 32 32 32V214.9L202.3 406.2c-6.7 10.9-10.3 23.5-10.3 36.4c0 38.3 31.1 69.4 69.4 69.4H570.6c38.3 0 69.4-31.1 69.4-69.4c0-12.8-3.6-25.4-10.3-36.4L512 214.9V64c17.7 0 32-14.3 32-32s-14.3-32-32-32H480zM384 224V64h64V224c0 5.9 1.6 11.7 4.7 16.8L501.5 320h-171l48.8-79.2c3.1-5 4.7-10.8 4.7-16.8z"/></svg>
                .grow
                    b {{ $t('Warning!') }}
                    p {{ $t('The auto translation is an experimental feature. Results may not meet your expectation. Check the result carefully!') }}
        template(slot="footer" class="dialog-footer")
            .modal-footer
                .left
                    el-button(
                        size="mini"
                        icon="el-icon-fa-times fas"
                        :disabled="translateDialog.saving !== false"
                        @click="translateDialog.visibility = false"
                    ) {{ $t('Dismiss') }}
                .right
                    el-button(
                        v-if="translateDialog.supportedAutoTranslate"
                        size="mini"
                        type="warning"
                        icon="el-icon-fa-flask fas"
                        :disabled="translateDialog.loading || ! [false, 'auto-translate'].includes(translateDialog.saving)"
                        :loading="translateDialog.saving === 'auto-translate'"
                        @click="createTranslation('auto-translate')"
                    ) {{ $t('Translate automatically') }}
                    el-button(
                        size="mini"
                        plain
                        type="primary"
                        :disabled="translateDialog.loading || ! [false, 'create-empty'].includes(translateDialog.saving)"
                        :loading="translateDialog.saving === 'create-empty'"
                        @click="createTranslation('create-empty')"
                    ) {{ $t('Create empty translation') }}
                    el-button(
                        size="mini"
                        type="primary"
                        :disabled="translateDialog.loading || ! [false, 'copy-origin'].includes(translateDialog.saving)"
                        :loading="translateDialog.saving === 'copy-origin'"
                        @click="createTranslation('copy-origin')"
                    ) {{ $t('Duplicate default language') }}
</template>

<i18n>
{
    "en": {
        "Default Language ({code})": "Default Language ({code})",
        "How should the menu item be translated into <b>{language}</b>?": "How should the menu item be translated into <b>{language}</b>?"
    },
    "de": {
        "Menu Item could not be fetched.": "Menüpunkt konnte nicht abgerufen werden.",
        "Menu Item could not be found.": "Menüpunkt konnte nicht gefunden werden.",
        "Something went wrong! Here are some menu items that could not be positioned inside the tree.": "Etwas ist schief gelaufen! Hier sind einige Menüpunkte, die nicht innerhalb des Hierarchie-Baums positioniert werden konnten.",
        "Menu Item Settings": "Menüpunkt-Einstellungen",
        "Save Settings": "Einstellungen speichern",
        "Save Redirect": "Weiterleitung speichern",
        "Save Meta": "Metadaten speichern",
        "Meta saved successfully.": "Metadaten erfolgreich gespeichert.",
        "Meta could not be saved.": "Metadaten konnten nicht gespeichert werden.",
        "Save Referenced Content": "Fremdinhalt speichern",
        "Settings saved successfully.": "Einstellungen erfolgreich gespeichert.",
        "Settings could not be saved.": "Einstellungen konnten nicht gespeichert werden.",
        "Marketing": "Marketing",
        "Save Marketing": "Marketing speichern",
        "Marketing saved successfully.": "Marketing erfolgreich gespeichert.",
        "Marketing could not be saved.": "Marketing konnten nicht gespeichert werden.",
        "Redirect saved successfully.": "Weiterleitung erfolgreich gespeichert.",
        "Redirect could not be saved.": "Weiterleitung konnten nicht gespeichert werden.",
        "Referenced content saved successfully.": "Fremdinhalt erfolgreich gespeichert.",
        "Referenced content could not be saved.": "Fremdinhalt konnten nicht gespeichert werden.",
        "Statistics": "Statistiken",
        "Target": "Diese Seite beobachten",
        "Marketing Campaign": "Marketingkampagne",
        "Marketing Campaign Landingpage": "Landingpage einer Marketingkampagne",
        "Language": "Sprache",
        "Translation": "Übersetzung",
        "Translation could not be created.": "Übersetzung konnte nicht erstellt werden.",
        "Translation deleted successfully.": "Übersetzung erfolgreich gelöscht.",
        "Translation could not be deleted.": "Übersetzung konnte nicht gelöscht werden.",
        "Translate Menu Item": "Menüpunkt übersetzen",
        "Translate automatically": "Automatisch übersetzen",
        "Create empty translation": "Leere Übersetzung erstellen",
        "Duplicate default language": "Standard-Sprache kopieren",
        "How should the menu item be translated into <b>{language}</b>?": "Wie soll der Menüpunkt in die Sprache <b>{language}</b> übersetzt werden?",
        "Warning!": "Achtung!",
        "The auto translation is an experimental feature. Results may not meet your expectation. Check the result carefully!": "Die automatische Übersetzung ist ein experimentelles Feature. Die Ergebnisse entsprechen möglicherweise nicht Ihren Erwartungen. Überprüfen Sie das Ergebnis sorgfältig!",
        "Default": "Standard",
        "Default Language ({code})": "Standardsprache ({code})",
        "New": "Neu",
        "Content": "Inhalt",
        "Content from": "Inhalt von",
        "Redirect": "Weiterleitung",
        "Meta": "Meta",
        "URL Key": "URL Schlüssel",
        "Copy URL": "URL kopieren",
        "URL copied to clipboard!": "URL wurde in die Zwischenablage kopiert!",
        "URL could not be copied.": "URL konnte nicht kopiert werden.",
        "No menu item selected!": "Kein Menüpunkt ausgewählt!",
        "Page Type": "Seitentyp",
        "Homepage": "Startseite",
        "for a single domain only": "nur für eine bestimmte Domain",
        "Homepage Domain": "Startseiten-Domain",
        "Everybody": "Jedem",
        "Members": "Mitgliedern",
        "Guests": "Gästen",
        "Any": "Alle",
        "Only members with the following tags can access this menu item": "Nur Mitglieder mit den folgenden Tags haben Zugang zu diesem Menüpunkt",
        "Select tags": "Tags auswählen",
        "Accessible by": "Zugänglich von",
        "loading data...": "Daten werden geladen..."
    }
}
</i18n>

<script>
import Vue from 'vue'

import _ from 'lodash';
import copy from 'copy-text-to-clipboard';
import uniqid from 'uniqid';
// import draggable from 'vuedraggable'
import { useAuthStore, useSetupStore, useMenuStore, usePageStore } from '@/stores';
import { checkRestrictions } from '@/helpers';

import { compareAsc, compareDesc, addDays, getUnixTime, fromUnixTime, toDate } from 'date-fns';
import { appFromUnixTime, appGetUnixTime } from '@/utils/timezone';

import { language, languageFlag, languageNativeName } from '@/filters'

import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';

import startOfWeek from 'date-fns/startOfWeek'
import endOfWeek from 'date-fns/endOfWeek'

import NestedSet from '@/nested-set-lib';

import router from '@/router';
import store from '@/store';

import MenuApi from '@/store/api/cms/menu';

import Main from '@/components/Page/Main';
import Meta from '@/components/Page/Meta';
import Redirect from '@/components/Page/Redirect';
import Reference from '@/components/Page/Reference';
import PageViewsChart from '@/components/Page/PageViewsChart';

import InputField from '@/components/Page/Sections/Partials/InputField'

export default {
    name: 'MenuItem',
    data() {
        return {
            error: false,
            notFound: false,
            pathNotValid: false,
            loading: false,
            saving: false,
            // dragging: false,

            collapseItem: ['content'],
            // pendingSection: undefined,

            restrictions: [
                {
                    key: 'everybody',
                    value: null,
                    name: this.$t('Everybody')
                },
                {
                    key: 'members',
                    value: 'members',
                    name: this.$t('Members')
                },
                {
                    key: 'guests',
                    value: 'guests',
                    name: this.$t('Guests')
                }
            ],

            usedPaths: [],

            // activeTranslation: undefined,

            pageviewStattisticsWidget: {
                id: uniqid('new:'),
                name: 'Marketing Target Views',
                settings: {
                    period: 'hour',
                    start: appGetUnixTime(startOfDay(new Date, { weekStartsOn: 1 })),
                    end: appGetUnixTime(endOfDay(new Date, { weekStartsOn: 1 })),
                    height: 300,
                    domain: undefined,
                }
            },

            translateDialog: {
                visibility: false,
                loading: false,
                saving: false,
                supportedAutoTranslate: false,
                form: {
                    menuItem: undefined,
                    type: undefined, // create-empty, copy-origin, auto-translate
                    language: undefined,
                }
            },
        };
    },
    components: {
        Main,
        Meta,
        Redirect,
        Reference,
        PageViewsChart,
        InputField,
    },
    watch: {
        hideMain(newValue, oldValue) {
            this.updateCollapseItem(!newValue && oldValue)
        },
        restriction(value) {
            if (value === 'member-tags') {
                const i = this.restrictions.findIndex(el => el.key === 'members')
                if (i >= 0) {
                    this.$set(this.restrictions[i], 'key', 'member-tags')
                    this.$set(this.restrictions[i], 'value', 'member-tags')
                }
            } else {
                const i = this.restrictions.findIndex(el => el.key === 'member-tags')
                if (i >= 0) {
                    this.$set(this.restrictions[i], 'key', 'members')
                    this.$set(this.restrictions[i], 'value', 'members')
                }
            }
        }
    },
    computed: {
        i18nFeatureEnabled() {
            let features = _.get(this.activeCms, 'config.template.features') || [];
            return !!features.find(el => el === 'i18n');
        },
        i18n() {
            if (this.i18nFeatureEnabled) {
                return _.get(this.activeCms, 'i18n')
            }
        },
        allowedToTranlsate() {
            let allowed = true;
            if (!this.isSuperUser && !this.isOwner) {
                allowed = this.rights.find(right => {
                    return right.key === 'translations'
                        && right.permission.create
                        && right.permission.update
                        && right.permission.delete
                })
            }
            return allowed;
        },
        languageOptions() {
            return _.get(this.activeCms, 'languages') || []
        },
        defaultLanguage() {
            return this.languageOptions.find(language => language.default)
        },
        activeTranslation: {
            get() {
                return this.pageUI.activeTranslation
            },
            set(translation) {
                this.setActiveTranslation(translation)
            }
        },
        language: {
            get() {
                return this.translateDialog.form.language || _.get(this.activeTranslation, 'language') || this.defaultLanguage
            },
            async set(value) {
                this.loading = true

                // check if translation is new
                // show dialog
                // - empty translation
                // - copy from default language
                // - translate automatically (if language is supported)
                // otherwise load translation

                const translation = this.findTranslation(value.code)

                if (value.code === this.defaultLanguage.code) {
                    // default language
                    // translation origin

                    this.activeTranslation = undefined

                    const menuItem = await MenuApi.fetchItem(this.currentMenuItem.id)

                    const page = _.get(menuItem, 'page')
                    if (page) {
                        this.insertPage(page)
                    } else {
                        this.$notify.error({
                            title: this.$t('An error occurred!'),
                            message: this.$t('Menu Item could not be fetched.'),
                            iconClass: 'fas el-icon-fa-xmark',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                } else if (!translation) {
                    // missing translation
                    // create a new one

                    this.translateDialog.form.menuItem = { id: this.currentMenuItem.id }
                    this.translateDialog.form.language = value

                    this.translateDialog.visibility = true
                    this.translateDialog.loading = true

                    const languages = this.libretranslate

                    if (languages && languages.length) {
                        const language = languages.find(el => el.code === this.defaultLanguage.code)
                        if (language && language.targets.includes(value.code)) {
                            this.translateDialog.supportedAutoTranslate = true
                        }
                    }

                    this.translateDialog.loading = false
                } else if (this.currentMenuItemIndex != null) {
                    // load existing translation

                    this.activeTranslation = translation

                    const menuItem = await MenuApi.fetchTranslation(translation.id)
                        .catch(() => {
                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Menu Item could not be fetched.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        })

                    const page = _.get(menuItem, 'page')
                    if (page) this.insertPage(page)
                }

                this.loading = false
            },
        },
        menuItemForm() {
            return this.currentMenuItem.pending
        },
        settingRules() {
            const rules = {
                title: 'required',
                path: `required|absolute_path|excluded:${this.usedPaths.join(',')}`
            }

            if (this.homepage && this.homepageRestriction === 'domain') {
                rules.homepageDomain = 'required'
            }

            return rules
        },
        title: {
            get() {
                if (this.activeTranslation) return this.activeTranslation.title
                return _.get(this.menuItemForm, 'title', '');
            },
            set(value) {
                if (this.activeTranslation) {
                    Vue.set(this.activeTranslation, 'title', value)
                } else if (this.currentMenuItemIndex != null) {
                    const i = this.currentMenuItemIndex;
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.title`, value });
                    this.pageStore.setState({ path: 'form.title', value });
                }
            }
        },
        path: {
            get() {
                if (this.activeTranslation) return this.activeTranslation.path
                return _.get(this.menuItemForm, 'path', '');
            },
            set(value) {
                if (this.activeTranslation) {
                    Vue.set(this.activeTranslation, 'path', value)
                } else if (this.currentMenuItemIndex != null) {
                    const i = this.currentMenuItemIndex;
                    // store.commit('menu/form/path', { i, value });
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.path`, value });
                }
            }
        },
        view: {
            get() {
                if (this.activeTranslation) return _.get(this.pageForm, 'translationOrigin.view', '');
                return _.get(this.pageForm, 'view', '');
            },
            set(value) {
                if (!this.activeTranslation && this.currentMenuItemIndex != null) {
                    // const i = this.currentMenuItemIndex;

                    switch (value) {
                        case 'Redirect':
                            this.pageStore.addHyperlink();
                            break;
                    }

                    this.pageStore.setState({ path: 'form.view', value });

                    const pageExtension = this.pageForm.extension;
                    const configExtension = this.config.extension;

                    if (configExtension && configExtension.key) {
                        const extension = this.activeCms.extensions
                            .find(extension => extension.key === configExtension.key) || null;
                        // store.commit('menu/form/changePageExtension', { menuItemIndex: i, extension });
                        this.pageStore.setState({ path: 'form.extension', value: extension });
                    } else if (pageExtension) {
                        // store.commit('menu/form/changePageExtension', { menuItemIndex: i, extension: null });
                        this.pageStore.setState({ path: 'form.extension', value: null });
                    }
                }
            }
        },
        restriction: {
            get() {
                return _.get(this.menuItemForm, 'restriction');
            },
            set(value) {
                if (this.currentMenuItemIndex != null) {
                    const i = this.currentMenuItemIndex;
                    // store.commit('menu/form/menuItemRestriction', { i, value });
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.restriction`, value });
                }
            }
        },
        memberTags: {
            get() {
                let tags = _.get(this.menuItemForm, 'memberTags');
                return tags.map(tag => {
                    if (tag.id.startsWith('new:')) return tag.name
                    else return tag
                })
            },
            set(tags) {
                if (this.currentMenuItemIndex != null) {
                    let memberTags = []
                    tags.forEach(tag => {
                        if (typeof tag === 'string' || tag instanceof String) {
                            memberTags.push({
                                id: uniqid('new:'),
                                name: tag,
                            })
                        } else {
                            memberTags.push(tag)
                        }
                    })

                    const i = this.currentMenuItemIndex;
                    // store.commit('menu/form/menuItemMemberTags', { i, value: memberTags });
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.memberTags`, value: memberTags });
                }
            }
        },
        homepage: {
            get() {
                return _.get(this.menuItemForm, 'homepage') || false;
            },
            set(value) {
                if (this.currentMenuItemIndex != null) {
                    // if (value) {
                    //     const menuItems = _.get(this.menuForm, 'menuItems') || [];
                    //     menuItems.forEach((item, i) => {
                    //         if (item.homepage && i !== this.currentMenuItemIndex) {
                    //             // store.commit('menu/form/homepage', { i, value: false });
                    //             store.commit('menu/set', { path: `form.menuItems.${i}.homepage`, value: false });
                    //         }
                    //     });
                    // }
                    const i = this.currentMenuItemIndex;
                    // store.commit('menu/form/homepage', { i, value });
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.homepage`, value });

                    if (!value) this.homepageRestriction = null;
                    else this.restriction = null;
                    // this.fixHomepageCollisions()
                }
            }
        },
        homepageRestriction: {
            get() {
                return _.get(this.menuItemForm, 'homepage_restriction');
            },
            set(value) {
                if (this.currentMenuItemIndex != null) {
                    const i = this.currentMenuItemIndex;
                    // store.commit('menu/form/homepageRestriction', { i, value });
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.homepage_restriction`, value });
                    if (value !== 'domain') this.homepageDomain = null;

                    // this.fixHomepageCollisions()
                }
            }
        },
        homepageDomain: {
            get() {
                return _.get(this.menuItemForm, 'homepageDomain');
            },
            set(value) {
                if (this.currentMenuItemIndex != null) {
                    const i = this.currentMenuItemIndex;
                    // store.commit('menu/form/homepageDomain', { i, value });
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.homepageDomain`, value });

                    // this.fixHomepageCollisions()
                }
            }
        },
        currentMenuItem() {
            let currentMenuItem = null
            const menuItems = _.get(this.menuForm, 'menuItems') || [];
            if (this.currentMenuItemIndex != null) {
                currentMenuItem = menuItems[this.currentMenuItemIndex];
            }

            return currentMenuItem;
        },
        // isOrphan() {
        //     const items = _.get(this.menuForm, 'menuItems') || [];
        //     const ancestors = NestedSet.getAncestors(items, this.currentMenuItem).map(item => item.id);

        //     return this.currentMenuItem.id === '__orphans' || ancestors.includes('__orphans');
        // },
        config() {
            return _.get(this.activeCms, 'config.template.pages', []).find(item => item.view === this.view);
        },
        sectionOptions() {
            const sections = _.get(this.config, 'sections') || [];

            let options = [];
            sections.forEach(section => {
                const config = _.get(this.activeCms, 'config.template.sections', []).find(
                    item => item.key === section.key
                );

                const option = { ...config, ...section };
                // TODO: initialize section
                options.push(option);
            });

            return options;
        },
        currentMenuItemIndex() {
            const menuItems = _.get(this.menuForm, 'menuItems') || [];
            const currentID = this.$route.params.id;

            if (menuItems.length && currentID) {
                const index = menuItems.findIndex(item => item.id.toString() === currentID.toString());
                if (index >= 0) return index;
                else return null;
            } else {
                return null;
            }
        },
        status: {
            get() {
                let enabled = true;

                const start = this.publishStart;
                if (start && enabled) enabled = compareAsc(new Date(), start) > 0;

                const end = this.publishEnd;
                // if (end && enabled) enabled = compareDesc(new Date(), addDays(end, 1)) > 0
                if (end && enabled) enabled = compareDesc(new Date(), end) > 0;

                return enabled;
            },
            set(active) {
                const start = this.publishStart;
                const end = this.publishEnd;

                if (active) {
                    if (start && compareAsc(new Date(), start) < 0) this.publishStart = startOfDay(new Date());
                    else if (end && compareDesc(new Date(), end) < 0) this.publishEnd = null;
                } else {
                    this.publishEnd = startOfDay(new Date());
                }

                if (this.currentMenuItemIndex != null) {
                    const i = this.currentMenuItemIndex;
                    // store.commit('menu/form/disabled', { i, value: !active });
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.diabled`, value: !active });
                }
            }
        },
        visible: {
            get() {
                const hidden = _.get(this.menuItemForm, 'hidden') || false;
                return !hidden;
            },
            set(visible) {
                // if (visible) this.unhideItem(this.currentMenuItem);
                // else this.hideItem(this.currentMenuItem);

                if (this.currentMenuItemIndex != null) {
                    const i = this.currentMenuItemIndex;
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.hidden`, value: !visible });
                }
            }
        },
        marketingTarget: {
            get() {
                if (this.activeTranslation) return _.get(this.pageForm, 'translationOrigin.is_marketing_target') || false;
                return _.get(this.pageForm, 'is_marketing_target') || false;
            },
            set(value) {
                if (!this.activeTranslation) {
                    // const i = this.currentMenuItemIndex;
                    // store.commit('menu/form/marketingTarget', { i, value });
                    this.pageStore.setState({ path: 'form.is_marketing_target', value });
                }
            }
        },
        marketingCampaignLandingPage: {
            get() {
                if (this.activeTranslation) return _.get(this.pageForm, 'translationOrigin.is_marketing_campaign_landing_page') || false;
                return _.get(this.pageForm, 'is_marketing_campaign_landing_page') || false;
            },
            set(value) {
                if (!this.activeTranslation) {
                    // const i = this.currentMenuItemIndex;
                    // store.commit('menu/form/marketingCampaignLandingPage', { i, value });
                    this.pageStore.setState({ path: 'form.is_marketing_campaign_landing_page', value });
                }
            }
        },
        marketingCampaign: {
            get() {
                if (this.activeTranslation) return _.get(this.pageForm, 'translationOrigin.marketingCampaign');
                return _.get(this.pageForm, 'marketingCampaign');
            },
            set(value) {
                if (!this.activeTranslation) {
                    // const i = this.currentMenuItemIndex;
                    // store.commit('menu/form/marketingCampaign', { i, value });
                    this.pageStore.setState({ path: 'form.marketingCampaign', value });
                }
            }
        },
        publishStart: {
            get() {
                let start = _.get(this.pageForm, 'publish_start');
                if (this.activeTranslation) start = _.get(this.pageForm, 'translationOrigin.publish_start');
                return start != null ? startOfDay(appFromUnixTime(start)) : '';
            },
            set(value) {
                if (!this.activeTranslation && this.currentMenuItemIndex != null) {
                    const i = this.currentMenuItemIndex;
                    // const start = value != null ? Math.round(value.getTime() / 1000) : null;
                    // const start = value != null ? getUnixTime(value) : null;
                    // store.commit('menu/form/publishStart', { i, value: start });
                    this.pageStore.setState({ path: 'form.publish_start', value: value != null ? appGetUnixTime(toDate(value)).toString() : null });

                    // store.commit('menu/form/disabled', { i, value: compareAsc(new Date(), value) <= 0 });
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.diabled`, value: compareAsc(new Date(), value) <= 0 });
                }
            }
        },
        publishEnd: {
            get() {
                let end = _.get(this.pageForm, 'publish_end');
                if (this.activeTranslation) end = _.get(this.pageForm, 'translationOrigin.publish_end');
                return end != null ? startOfDay(appFromUnixTime(end)) : '';
            },
            set(value) {
                if (!this.activeTranslation && this.currentMenuItemIndex != null) {
                    const i = this.currentMenuItemIndex;
                    // const end = value != null ? getUnixTime(value) : null;
                    // store.commit('menu/form/publishEnd', { i, value: end });
                    this.pageStore.setState({ path: 'form.publish_end', value: value != null ? appGetUnixTime(toDate(value)).toString() : null });

                    // store.commit('menu/form/disabled', { i, value: compareDesc(new Date(), value) <= 0 });
                    this.menuStore.setState({ path: `form.menuItems.${i}.pending.diabled`, value: compareDesc(new Date(), value) <= 0 });
                }
            }
        },
        // currentCollapseItem: {
        //     get() {
        //         // change collapseItem to "settings" if there are no section options availible
        //         if (this.hideMain) return 'settings';
        //         return this.collapseItem;
        //     },
        //     set(value) {
        //         this.collapseItem = value;
        //     }
        // },
        hideMain() {
            const areas = _.get(this.config, 'grid.areas') || [];
            return (
                this.view !== 'Reference' &&
                !this.sectionOptions.length &&
                !areas.filter(area => area.sections && area.sections.length).length
            );
        },
        hideMeta() {
            return this.view === 'Redirect' || this.view === 'Logout';
        },
        sections() {
            return _.get(this.pageForm, 'sections') || [];
        },
        marketingTitle() {
            let title = this.$t('Marketing');
            if (!this.enabledExtensions.includes('marketing')) {
                title = this.$t('Statistics');
            }
            return title;
        },
        settings() {
            return _.get(this.config, 'settings') || []
        },
        settingValues() {
            let settingValues = {}
            let options = {}

            // menu item settings
            const settings = _.get(this.currentMenuItem, 'settings') || []

            // select fields
            // get default values
            // so that invalid values can be determined
            if (this.settings && this.settings.length) {
                this.settings.forEach(setting => {
                    if (setting.options && setting.options.length) {
                        // const option = setting.options.find(option => option.default === true)
                        setting.options.forEach(option => {
                            if (option.default === true && ! settings.find(el => el.key === setting.key)) {
                                if (['multi-select', 'tags'].includes(setting.type)) {
                                    if (! settingValues[setting.key]) settingValues[setting.key] = []
                                    settingValues[setting.key].push({ key: option.key, value: option.value })
                                }
                                else settingValues[setting.key] = option.key
                            }
                            _.set(options, `${setting.key}.${option.key}`, option.value)
                        })
                    } else {
                        switch (setting.type) {
                            case 'checkbox':
                            case 'switch':
                                settingValues[setting.key] = false
                                if (setting.value) {
                                    settingValues[setting.key] = ['true', '1', 'yes', true].includes(setting.value)
                                }
                                break;

                            default:
                                settingValues[setting.key] = setting.value
                        }
                    }
                })
            }

            // override with actual values
            if (this.currentMenuItem.settings && this.currentMenuItem.settings.length) {
                this.currentMenuItem.settings.forEach(setting => {
                    // check if selected option exists

                    // get related configuration (defined in temaplte JSON)
                    const config = this.settings.find(el => el.key === setting.key)
                    const type = _.get(config, 'type')

                    switch (type) {
                        case 'select':
                        case 'multi-select':
                        case 'tags':
                            if (setting.key && setting.value != null && _.get(options, `${setting.key}.${setting.value}`) !== undefined) {
                                settingValues[setting.key] = setting.value
                            } else if (setting.options && setting.options.length) {
                                settingValues[setting.key] = setting.options
                            }
                            break;

                        case 'checkbox':
                        case 'switch':
                            settingValues[setting.key] = false
                            if (setting.value) {
                                settingValues[setting.key] = ['true', '1', 'yes', true].includes(setting.value)
                            }
                            break;

                        default:
                            settingValues[setting.key] = setting.value
                    }
                })
            }

            return settingValues
        },
        rights() {
            return useAuthStore().rights;
        },
        isSuperUser() {
            return useAuthStore().isSuperUser;
        },
        isOwner() {
            return useAuthStore().isOwner;
        },
        locale() {
            return useSetupStore().locale;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        effectiveExtensions() {
            return useSetupStore().effectiveExtensions;
        },
        menuStore() {
            return useMenuStore();
        },
        menuForm() {
            return this.menuStore.form;
        },
        menuRules() {
            return this.menuStore.rules;
        },
        menuOptions() {
            return this.menuStore.options;
        },
        menuValidation() {
            return this.menuStore.validation;
        },
        pageStore() {
            return usePageStore();
        },
        pageForm() {
            return this.pageStore.form;
        },
        pageUI() {
            return this.pageStore.ui;
        },
        pageOptions() {
            return this.pageStore.options;
        },
        libretranslate() {
            return useSetupStore().libretranslate;
        },
        publicUrl() {
            const baseUrl = _.get(this.activeCms, 'domains.0.base_url', '');
            const path = this.path;
            if (!baseUrl || !path) return '';

            let protocol = 'http:';
            if (process.env.NODE_ENV === 'production') protocol = window.location.protocol;

            const normalizedBaseUrl = String(baseUrl)
                .replace(/^https?:\/\//, '')
                .replace(/\/+$/, '');

            return `${protocol}//${normalizedBaseUrl}${path}`;
        },
    },
    methods: {
        async copyPublicUrl() {
            if (!this.publicUrl) return;

            try {
                if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(this.publicUrl);
                } else {
                    copy(this.publicUrl);
                }
                this.$message.success(this.$t('URL copied to clipboard!'));
            } catch (e) {
                try {
                    copy(this.publicUrl);
                    this.$message.warning(this.$t('URL copied to clipboard!'));
                } catch (fallbackError) {
                    this.$message.error(this.$t('URL could not be copied.'));
                }
            }
        },
        checkRestrictions,
        updateCollapseItem(openContent = false) {
            if (this.hideMain) {
                this.collapseItem = this.collapseItem.filter(item => item !== 'content')
                if (!this.collapseItem.includes('settings')) this.collapseItem.push('settings')
            } else if (openContent) {
                if (!this.collapseItem.includes('content')) this.collapseItem.push('content')
            }
        },
        // fixHomepageCollisions() {
        //     // fix homepage collisions
        //     if (this.homepage) {
        //         const menuItems = _.get(this.menuForm, 'menuItems') || [];
        //         menuItems.forEach((item, i) => {
        //             if (item.homepage && i !== this.currentMenuItemIndex) {
        //                 // store.commit('menu/form/homepage', { i, value: false });
        //                 store.commit('menu/set', { path: `form.menuItems.${i}.homepage`, value: false });
        //             }
        //         });
        //     }
        // },
        async createTranslation(type = 'copy-origin') {
            this.translateDialog.form.type = type
            this.translateDialog.saving = type

            const translation = await this.translateMenuItem(this.translateDialog.form)
                .catch(() => {
                    this.translateDialog.saving = false
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Translation could not be created.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })

            // add translation to menu item
            if (translation) {
                this.addTranslation({ index: this.currentMenuItemIndex, translation })

                this.activeTranslation = translation

                const menuItem = await MenuApi.fetchTranslation(translation.id)
                    .catch(() => {
                        this.translateDialog.saving = false
                        this.$notify.error({
                            title: this.$t('An error occurred!'),
                            message: this.$t('Menu Item could not be fetched.'),
                            iconClass: 'fas el-icon-fa-xmark',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    })

                const page = _.get(menuItem, 'page')
                if (page) this.insertPage(page)
            }

            this.translateDialog.saving = false
            this.translateDialog.visibility = false
        },
        async deleteTranslation(translation) {
            const payload = {
                id: this.menuForm.id,
                menuItems: [{
                    id: this.currentMenuItem.id,
                    translations: this.currentMenuItem.translations.map(el => {
                        if (el.id === translation.id) el.deleted = true
                        return el
                    })
                }]
            }

            this.saving = 'delete-translation'
            this.menuStore.save(payload)
                .then(menu => {
                    this.language = this.defaultLanguage
                    this.removeTranslation({ index: this.currentMenuItemIndex, translation })

                    this.$notify.success({
                        title: this.$t('Translation deleted successfully.'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                })
                .catch(error => {
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Translation could not be deleted.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                    console.error(error)
                })
                .finally(() => this.saving = false)
        },
        resetTranslateDialog() {
            this.translateDialog = {
                visibility: false,
                loading: false,
                saving: false,
                supportedAutoTranslate: false,
                form: {
                    menuItem: undefined,
                    type: undefined,
                    language: undefined,
                }
            }
        },
        getLanguageName(code) {
            return language(code, this.locale ? this.locale.value : 'de')
        },
        getLanguageFlag(code) {
            return languageFlag(code)
        },
        getLanguageNativeName(code) {
            return languageNativeName(code)
        },
        findTranslation(code) {
            const translations = this.currentMenuItem.translations || []
            return translations.find(el => _.get(el, 'language.code') === code)
        },
        checkConditions(setting) {
            let valid = true

            // eslint-disable-next-line no-unused-vars
            const settings = this.settingValues;
            const enabledExtensions = this.enabledExtensions;
            try {
                const conditions = setting.conditions
                if (conditions && conditions.length) {
                    conditions.forEach(o => {
                        if (valid === false) return

                        const { type, condition } = o
                        switch (type) {
                            case 'eval':
                                valid = eval(condition)
                                break;

                            default:
                                valid = false
                        }
                    })
                }
            } catch (error) {
                // eslint-disable-next-line
                console.error(error);
                valid = false
            }

            return valid
        },
        disabledPublishStartDate(start) {
            const end = this.publishEnd;
            if (end) return compareAsc(addDays(start, 1), end) > 0;
            else return false;
        },
        disabledPublishEndDate(end) {
            const start = this.publishStart;
            if (start) return compareDesc(end, addDays(start, 1)) > 0;
            else return false;
        },
        setPageviewStatisticWidgetValue({ key, value }) {
            this.$set(this.pageviewStattisticsWidget, key, value);
        },
        toggleMemberRestriction(value) {
            if (value === true) this.restriction = 'members'
            else this.restriction = 'member-tags'
        },
        toggleHomepageRestriction(value) {
            if (value === true) {
                this.homepageRestriction = 'domain'
            } else {
                this.homepageRestriction = null
            }

            // store.commit('menu/resetRules')
        },
        updateSetting(payload) {
            const i = this.currentMenuItemIndex;

            if (payload.value && _.isArray(payload.value)) {
                this.menuStore.setSettingsOptions({ i, key: payload.key, options: payload.value });
            } else {
                this.menuStore.setSettingsValue({ i, ...payload });
            }
        },
        saveSettings() {
            this.$refs.settingsForm.validate(async valid => {
                if (valid) {
                    // payload for the saveMenu mutation
                    let payload = {
                        id: this.menuForm.id,
                        menuItems: [{
                            id: this.currentMenuItem.id,
                            title: this.title,
                            path: this.path,
                            homepage: this.homepage,
                            homepage_restriction: this.homepageRestriction,
                            homepageDomain: this.homepageDomain,
                            restriction: this.restriction,
                            memberTags: this.memberTags,
                            hidden: !this.visible,
                            settings: this.currentMenuItem.settings,
                            page: {
                                id: this.pageForm.id,
                                view: this.view,
                                extension: this.pageForm.extension,
                                publish_start: this.publishStart != '' ? appGetUnixTime(this.publishStart) : null,
                                publish_end: this.publishEnd != '' ? appGetUnixTime(this.publishEnd) : null,
                            }
                        }]
                    }

                    if (this.activeTranslation) {
                        payload = {
                            id: this.menuForm.id,
                            menuItems: [{
                                id: this.activeTranslation.id,
                                title: this.activeTranslation.title,
                                path: this.activeTranslation.path,
                            }]
                        }
                    }

                    this.saving = 'settings'
                    this.menuStore.save(payload)
                        .then(menu => {

                            // current index must be re-evaluated
                            // bacause the menu items order can be different in the saving result

                            const menuItems = _.get(menu, 'menuItems') || [];
                            const currentID = this.$route.params.id;

                            let i
                            if (menuItems.length && currentID) {
                                i = menuItems.findIndex(item => item.id.toString() === currentID.toString());
                                if (i < 0) i = null
                            }

                            if (i != null) {
                                let currentMenuItem = _.cloneDeep(this.menuItemForm);
                                currentMenuItem.settings = _.cloneDeep(this.currentMenuItem.settings);
                                currentMenuItem.pending = _.cloneDeep(this.menuItemForm);

                                menu.menuItems[i] = currentMenuItem;
                                // this.menuStore.setState({ path: `form.menuItems.${i}`, value: currentMenuItem });
                            }

                            this.menuStore.setState({ path: 'form', value: menu });

                            this.$notify.success({
                                title: this.$t('Settings saved successfully.'),
                                iconClass: 'fas el-icon-fa-check',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000,
                            })
                        })
                        .catch(error => {
                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Settings could not be saved.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000,
                            })
                            console.error(error)
                        })
                        .finally(() => this.saving = false)
                }
            });
        },
        saveMarketing() {
            if (!this.enabledExtensions.includes('marketing')) return

            // payload for the savePage mutation
            const payload = {
                id: this.pageForm.id,
                is_marketing_target: this.marketingTarget,
                is_marketing_campaign_landing_page: this.marketingCampaignLandingPage,
                marketingCampaign: this.marketingCampaign,
            }

            this.saving = 'marketing'
            this.pageStore.save(payload)
                .then(page => {
                    this.$notify.success({
                        title: this.$t('Marketing saved successfully.'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                })
                .catch(error => {
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Marketing could not be saved.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                    console.error(error)
                })
                .finally(() => this.saving = false)
        },
        saveMeta() {
            // payload for the savePage mutation
            const payload = {
                id: this.pageForm.id,
                metas: this.pageForm.metas,
            }

            this.saving = 'meta'
            this.pageStore.save(payload)
                .then(page => {
                    this.$notify.success({
                        title: this.$t('Meta saved successfully.'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                })
                .catch(error => {
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Meta could not be saved.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                    console.error(error)
                })
                .finally(() => this.saving = false)
        },
        saveRedirect() {
            // payload for the savePage mutation
            const payload = {
                id: this.pageForm.id,
                view: this.view,
                redirect: this.pageForm.redirect,
            }

            this.saving = 'redirect'
            this.pageStore.save(payload)
                .then(page => {
                    this.$notify.success({
                        title: this.$t('Redirect saved successfully.'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                })
                .catch(error => {
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Redirect could not be saved.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                    console.error(error)
                })
                .finally(() => this.saving = false)
        },
        saveReference() {
            // payload for the savePage mutation
            const payload = {
                id: this.pageForm.id,
                view: this.view,
                reference: this.pageForm.reference,
            }

            this.saving = 'reference'
            this.pageStore.save(payload)
                .then(page => {
                    this.$notify.success({
                        title: this.$t('Referenced content saved successfully.'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                })
                .catch(error => {
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Referenced content could not be saved.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                    console.error(error)
                })
                .finally(() => this.saving = false)
        },
        populateUsedPaths() {
            this.usedPaths = _.cloneDeep(this.menuValidation.usedPaths);
            this.menuForm.menuItems.forEach(item => {
                if (item.id !== this.currentMenuItem.id) {
                    this.usedPaths.push(item.path)
                }
            });
        },
        fetchMenuItem(id) {
            return this.menuStore.fetchMenuItem(id);
        },
        translateMenuItem(data) {
            return this.menuStore.translateMenuItem(data);
        },
        insertPage(page) {
            return this.pageStore.insertPage(page);
        },
        hideItem(item) {
            this.menuStore.markItemAsHidden(item);
        },
        unhideItem(item) {
            this.menuStore.unmarkItemAsHidden(item);
        },
        addTranslation(data) {
            this.menuStore.addTranslationToMenuItem(data);
        },
        removeTranslation(data) {
            this.menuStore.removeTranslationFromMenuItem(data);
        },
        setActiveTranslation(translation) {
            this.pageStore.setActiveTranslation(translation);
        }
    },
    mounted() {
        const id = this.$route.params.id;

        if (id) {
            if (_.startsWith(id, 'new:')) {
                // TODO: create request to API

                // // check if the item already exists in state
                // const menuItems = _.get(this.menuForm, 'menuItems') || [];
                // const existing = menuItems.find(item => item.id === id);

                // // initialize new menu item
                // if (!existing || !existing.page) this.initializeMenuItem(id);

                // setTimeout(() => this.collapseItem = ['content'], 100)
            } else if (this.currentMenuItem && this.currentMenuItem.id !== '__orphans') {
                // load item if not already in store

                this.error = false;
                this.notFound = false;
                this.pathNotValid = false;

                this.loading = true;

                // const fetchDependencies = () => Promise.all([
                //     // ...
                // ])

                this.fetchMenuItem(id)
                    .then(data => {

                        this.populateUsedPaths()
                        this.updateCollapseItem()

                        // setTimeout(() => this.collapseItem = ['content'], 300)

                        // // store.commit('menu/resetUI')

                        // const allowedMenuItems = _.get(this.activeCms, 'config.template.pages', [])
                        // this.insertItemTypeOptions(allowedMenuItems)

                        // this.loading = false

                        // // fetch options for dropdowns
                        // fetchDependencies()
                        //     .catch(err => {
                        //         this.error = true

                        //         /* eslint no-console: ["error", { allow: ["error"] }] */
                        //         console.error(err)

                        //         this.$notify.error({
                        //             title: this.$t('An error occurred!'),
                        //             message: this.$t('Menu dependencies could not be fetched.'),
                        //             iconClass: 'fas el-icon-fa-xmark',
                        //             position: 'bottom-left',
                        //             customClass: 'dark',
                        //             duration: 8000,
                        //         })
                        //     })

                        // add tmp menu item copy into the menu
                        // to be able to undo unsaved changes on unmount

                        if (this.currentMenuItemIndex != null) {
                            const i = this.currentMenuItemIndex;
                            const currentMenuItem = _.cloneDeep(this.currentMenuItem);
                            this.menuStore.setState({ path: `form.menuItems.${i}.pending`, value: currentMenuItem });
                        }
                    })
                    .catch(err => {
                        this.error = true;
                        this.notFound = true;

                        /* eslint no-console: ["error", { allow: ["error"] }] */
                        console.error(err);

                        this.$notify.error({
                            title: this.$t('An error occurred!'),
                            message: this.$t('Menu Item could not be fetched.'),
                            iconClass: 'fas el-icon-fa-xmark',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    })
                    .finally(() => {
                        this.loading = false;
                    });

            } else {
                // setTimeout(() => this.collapseItem = ['content'], 100)
            }
        }
    },
    unmounted() {
        if (this.currentMenuItemIndex != null) {
            this.menuStore.unsetState({ path: `form.menuItems.${this.currentMenuItemIndex}.pending` });
        }
    },
    beforeDestroy() {
        this.activeTranslation = undefined
    },
};
</script>

<style lang="sass" scoped>
section.menu-item-form
    display: flex
    height: 100%
    justify-content: center
    align-items: center
    .menu-item-form
        display: flex
        flex-direction: column
    .menu-item-form,
    .el-collapse
        flex-grow: 1
        align-self: flex-start
        width: 100%
        height: 100%
    .el-alert
        align-self: flex-start
    .loader
        min-height: 400px
        width: 100%
        align-self: flex-start
section.menu-item-form.empty
    .el-collapse-item.content :deep()
        .el-collapse-item__content
            padding-bottom: 0
            & > .visible-grid
                margin-bottom: 25px
            & > .page-main
                padding-bottom: 25px
.content-status-flex-col
    &.datepickers
        max-width: 600px
    &.status-switch
        flex-grow: 1
.marketing-campaign-select
    margin-top: 1rem
    margin-bottom: 0

.under-construction
    display: flex
    flex-direction: column
    justify-content: center
    align-items: center
    padding: 4rem
    text-align: center
    i
        font-size: 4rem
        color: rgba(0,0,0,0.2)
    h4
        margin-top: 1rem
        margin-bottom: 0
        font-size: 2rem
        font-weight: 300
        color: rgba(0,0,0,0.3)


.pageview-chart
    margin-bottom: 1.2rem

.pageview-chart:deep() canvas
    min-height: 300px

.settings
    // hr
    //     border-top: none
    //     border-bottom: 1px solid lighten($color-grey-text, 30%)
    //     margin-top: 1.4rem
    //     margin-bottom: 1rem
    .custom-settings
        margin-top: 22px
        .setting + .setting
            margin-top: 22px
        &::v-deep
            .input-field.divider hr
                border-top: none
                border-bottom: 1px solid lighten($color-grey-text, 30%)

.url-key-input
    display: flex
    align-items: flex-start
    gap: 8px
    &::v-deep .el-input
        flex: 1
.url-copy-button
    flex-shrink: 0
    border-radius: 6px
</style>
